import type { OfferSummary } from '@/types/api'
import { Spinner } from '@/components/shared/Spinner'
import { Button } from '@/components/shared/Button'
import { OfferResetTag } from './OfferResetTag'
import { formatCap } from '@/utils/formatters'

interface OffersOverviewProps {
  offers: OfferSummary[]
  isLoading: boolean
  error?: string | null
  onSelect: (offerId: number) => void
  onRetry?: () => void
}

function barColor(pct: number): string {
  if (pct >= 95) return 'bg-brand-red'
  if (pct >= 75) return 'bg-brand-yellow'
  return 'bg-brand-green'
}

function Tag({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'muted' | 'blue' | 'red' }) {
  const tones = {
    muted: 'border-border-subtle text-text-muted',
    blue: 'border-brand-blue/30 text-brand-blue bg-brand-blue/5',
    red: 'border-brand-red/30 text-brand-red bg-brand-red/5',
  } as const
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-px text-[10px] font-medium uppercase tracking-wide ${tones[tone]}`}>
      {children}
    </span>
  )
}

/**
 * The dashboard's landing screen: every offer the viewer can run, one row each, with the
 * cap they hold on it right now. Picking one opens that offer's geos.
 *
 * This replaces the old single page that stacked EVERY offer's geo cards, overflow tiles and
 * request tiles on top of each other — with more than one offer live that page was a wall,
 * and the affiliate's actual first question ("which offer, and how much is left on it?")
 * was the one thing it never answered directly.
 */
export function OffersOverview({ offers, isLoading, error, onSelect, onRetry }: OffersOverviewProps) {
  if (isLoading && offers.length === 0) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="h-7 w-7" />
          <p className="text-sm text-text-muted">Loading offers…</p>
        </div>
      </div>
    )
  }

  if (error && offers.length === 0) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm text-text-secondary">{error}</p>
        {onRetry && <Button variant="secondary" onClick={onRetry}>Retry</Button>}
      </div>
    )
  }

  if (offers.length === 0) {
    return (
      <div className="rounded-2xl border border-border-subtle bg-surface-card px-5 py-12 text-center">
        <p className="text-sm text-text-secondary">No offers are available on your account yet.</p>
        <p className="mt-1 text-xs text-text-muted">Contact your account manager to get access.</p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border-subtle bg-surface-card shadow-xs">
      <div className="flex items-center justify-between gap-3 border-b border-border-subtle px-5 py-3">
        <h2 className="text-sm font-semibold tracking-tight text-text-primary">Offers</h2>
        <span className="text-[11px] text-text-muted">
          {offers.length} offer{offers.length === 1 ? '' : 's'} · select one to see its geos and cap
        </span>
      </div>
      <ul>
        {offers.map((offer) => {
          const hasCap = offer.cap_total > 0
          const requestable = offer.overflow_geo_count + offer.request_geo_count
          return (
            <li key={offer.id} className="border-b border-border-subtle last:border-b-0">
              <button
                onClick={() => onSelect(offer.id)}
                className="grid w-full cursor-pointer grid-cols-1 items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-gray-50 sm:grid-cols-[minmax(0,1fr)_minmax(0,320px)_auto]"
              >
                {/* Identity */}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-semibold text-text-primary">{offer.name}</span>
                    {offer.is_paused === 1 && <Tag tone="red">Paused</Tag>}
                    {offer.is_private === 1 && <Tag>Private</Tag>}
                    {offer.is_team_pooled && <Tag tone="blue">Shared pool</Tag>}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-text-muted">
                    <span>
                      {offer.geo_count} geo{offer.geo_count === 1 ? '' : 's'} with cap
                    </span>
                    {requestable > 0 && (
                      <>
                        <span className="text-text-placeholder">·</span>
                        <span>{requestable} to request</span>
                      </>
                    )}
                    <OfferResetTag offer={offer} showCountdown={false} compact />
                  </div>
                </div>

                {/* Cap */}
                <div className="min-w-0">
                  {hasCap ? (
                    <>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-base font-semibold tabular-nums text-text-primary">
                          {formatCap(offer.cap_remaining, offer.cap_unit)}
                        </span>
                        <span className="text-[11px] text-text-muted">left today</span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${barColor(offer.pct_used)}`}
                          style={{ width: `${offer.pct_used}%` }}
                        />
                      </div>
                      <p className="mt-1 text-[11px] text-text-muted">
                        {formatCap(offer.cap_used, offer.cap_unit)} used of{' '}
                        {formatCap(offer.cap_total, offer.cap_unit)} · {offer.pct_used}%
                      </p>
                    </>
                  ) : (
                    <p className="text-[11px] text-text-muted">
                      {requestable > 0 ? 'No cap yet — open to request some' : 'No cap on this offer'}
                    </p>
                  )}
                </div>

                <span className="hidden text-text-placeholder sm:block" aria-hidden>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
